import type { Node } from "@xyflow/react";
import type { FrameData } from "@/lib/frame-data";

export const SELECTED_FRAME_Z_BASE = 10_000;

function isSelectionZIndex(zIndex: number | undefined): boolean {
  return typeof zIndex === "number" && zIndex >= SELECTED_FRAME_Z_BASE;
}

/** Demote a selection-elevated zIndex into the band below the selection
 *  base instead of clearing it. Deselected frames keep their selection
 *  sequence as a durable zIndex so "last interacted stays on top"
 *  survives deselection — clearing to undefined lets DOM order restack
 *  overlapping frames invisibly (translucent glass), making right-click
 *  hit a different frame than the one the user perceives on top. */
export function demotedZIndex(zIndex: number | undefined): number | undefined {
  return isSelectionZIndex(zIndex)
    ? (zIndex as number) - SELECTED_FRAME_Z_BASE
    : zIndex;
}

/** React Flow-driven deselections (applyNodeChanges) flip `selected`
 *  without going through selectFrame, leaking zIndex ≥ base on
 *  unselected nodes — demote them to keep hit-testing consistent. */
export function demoteDeselectedNodeZ(node: Node<FrameData>): Node<FrameData> {
  if (node.selected || !isSelectionZIndex(node.zIndex)) return node;
  return { ...node, zIndex: demotedZIndex(node.zIndex) };
}
